import { isEmpty, unset } from 'lodash';
import { BehaviorSubject, Subject } from 'rxjs';
import { finalize, takeUntil, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { TranslocoService } from '@ngneat/transloco';
import { select, Store } from '@ngrx/store';

import { environment } from '../../../../environments/environment';
import { AuthNoticeService, currentUser, User, UserLoaded } from '../../../core/auth';
import { AppState } from '../../../core/reducers';
import { ConfirmPasswordValidator } from '../auth/confirm-password.validator';
import { ProfileService } from './profile.service';

@Component({
  selector: 'kt-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
})
export class ProfileComponent implements OnInit, OnDestroy {
  @ViewChild('avatarInput') avatarInput: ElementRef;
  profileForm: FormGroup;
  loading = false;
  errors: any = [];
  enableTfa: FormControl = new FormControl(false);
  tfaData: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  authcode = '';

  user: any = null;
  avatarStyle: BehaviorSubject<SafeStyle> = new BehaviorSubject<SafeStyle>('');
  private unsubscribe: Subject<any>; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/

  constructor(
    private authNoticeService: AuthNoticeService,
    private translocoService: TranslocoService,
    private store: Store<AppState>,
    private fb: FormBuilder,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private http: HttpClient,
    private service: ProfileService,
  ) {
    this.unsubscribe = new Subject();
  }

  ngOnInit() {
    this.initProfileForm();

    this.store.pipe(select(currentUser), takeUntil(this.unsubscribe)).subscribe((user) => {
      if (user) {
        this.user = user;
        unset(user, 'avatar');
        this.setAvatarStyle();
        this.enableTfa.setValue(user.tfa.enabled);
        this.tfaData.next(user.tfa);

        this.profileForm.patchValue({
          ...user,
          avatar: '',
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.loading = false;
  }

  initProfileForm() {
    this.profileForm = this.fb.group({
      firstName: ['', Validators.compose([Validators.required, Validators.minLength(3), Validators.maxLength(100)])],
      lastName: ['', Validators.compose([Validators.required, Validators.minLength(3), Validators.maxLength(100)])],
      email: [
        '',
        Validators.compose([
          Validators.required,
          Validators.email,
          Validators.minLength(3),
          // https://stackoverflow.com/questions/386294/what-is-the-maximum-length-of-a-valid-email-address
          Validators.maxLength(320),
        ]),
      ],
      currentPassword: '',
      password: '',
      passwordConfirmation: '',
      avatar: '',
    });

    this.profileForm
      .get('currentPassword')
      .valueChanges.pipe(takeUntil(this.unsubscribe))
      .subscribe((curr: string) => {
        if (curr) {
          this.profileForm.setValidators(
            ConfirmPasswordValidator.MatchPassword(!isEmpty(this.profileForm.get('currentPassword').value)),
          );

          this.profileForm
            .get('password')
            .setValidators(
              Validators.compose([Validators.required, Validators.minLength(3), Validators.maxLength(100)]),
            );

          this.profileForm
            .get('passwordConfirmation')
            .setValidators(
              Validators.compose([Validators.required, Validators.minLength(3), Validators.maxLength(100)]),
            );
        } else {
          this.profileForm.clearValidators();
          this.profileForm.get('password').clearValidators();
          this.profileForm.get('passwordConfirmation').clearValidators();
          this.profileForm.get('passwordConfirmation').setErrors(null);
        }
      });
  }

  submit() {
    // check form
    if (this.profileForm.invalid) {
      this.checkForm();
      return;
    }

    this.loading = true;
    const user = this.profileForm.getRawValue();

    if (user.avatar) {
      this.changeAvatar(this.avatarInput.nativeElement.files).subscribe(() => {
        unset(user, 'avatar');

        this.service
          .updateUser(this.user.id, user)
          .pipe(
            tap((res: User) => {
              if (res) {
                this.store.dispatch(new UserLoaded({ user: res }));
                // pass notice message to the login page
                this.authNoticeService.setNotice(this.translocoService.translate('PROFILE.UPDATE.SUCCESS'), 'success');
              } else {
                this.authNoticeService.setNotice(
                  this.translocoService.translate('AUTH.VALIDATION.INVALID_LOGIN'),
                  'danger',
                );
              }
            }),
            takeUntil(this.unsubscribe),
            finalize(() => {
              this.loading = false;
            }),
          )
          .subscribe();
      });
    } else {
      this.service
        .updateUser(this.user.id, user)
        .pipe(
          tap((res: User) => {
            if (res) {
              this.store.dispatch(new UserLoaded({ user: res }));
              // pass notice message to the login page
              if (user.currentPassword) {
                this.authNoticeService.setNotice(
                  this.translocoService.translate('PROFILE.PASSWORD_UPDATE.SUCCESS'),
                  'success',
                );
              } else {
                this.authNoticeService.setNotice(this.translocoService.translate('PROFILE.UPDATE.SUCCESS'), 'success');
              }
            } else {
              this.authNoticeService.setNotice(
                this.translocoService.translate('AUTH.VALIDATION.INVALID_LOGIN'),
                'danger',
              );
            }
          }),
          takeUntil(this.unsubscribe),
          finalize(() => {
            this.loading = false;
          }),
        )
        .subscribe();
    }
  }

  toggleTfa() {
    const checked = this.enableTfa.value;

    if (checked) {
      this.setupTfa();
    } else {
      this.disableTfa();
    }
  }

  setupTfa() {
    this.service
      .setupTfa(this.user)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((data) => {
        this.tfaData.next(data);
      });
  }

  verifyTfa() {
    this.service.verifyTfa(this.user, this.authcode).subscribe(
      ({ user }: { message: string; user: User }) => {
        this.authNoticeService.setNotice(this.translocoService.translate('USER.TFA.SETUP_SUCCESS'), 'success');
        this.tfaData.next(user.tfa);
        this.store.dispatch(new UserLoaded({ user }));
      },
      (err) => {
        if (err.status === 403) {
          this.authNoticeService.setNotice(this.translocoService.translate('USER.TFA.SETUP_ERROR'), 'danger');
        }
      },
    );
  }

  disableTfa() {
    this.service
      .deleteTfa(this.user)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((user) => {
        this.tfaData.next((user as any).tfa);
        this.store.dispatch(new UserLoaded({ user: user as any }));
      });
  }

  /**
   * Checking control validation
   *
   * @param controlName: string => Equals to formControlName
   * @param validationType: string => Equals to validators name
   */
  isControlHasError(controlName: string, validationType: string): boolean {
    const control = this.profileForm.controls[controlName];
    if (!control) {
      return false;
    }

    const result = control.hasError(validationType) && (control.dirty || control.touched);
    return result;
  }

  selectAvatar() {
    this.avatarInput.nativeElement.click();

    this.profileForm
      .get('avatar')
      .valueChanges.pipe(takeUntil(this.unsubscribe))
      .subscribe(() => {
        const input = this.avatarInput.nativeElement;
        if (input.files && input.files[0]) {
          const file = input.files[0];
          const reader = new FileReader();

          reader.onload = (event) => {
            const dataUri = (event.target as any).result;
            this.avatarStyle.next(this.sanitizer.bypassSecurityTrustStyle(`background-image: url(${dataUri});`));
          };

          reader.onerror = (event) => {
            console.error('Error while file reading! Error code: ' + (event.target as any).error.code);
          };

          reader.readAsDataURL(file);
        }
      });
  }

  changeAvatar(files: FileList) {
    return this.service.updateAvatar(this.user.id, files);
  }

  private checkForm() {
    const controls = this.profileForm.controls;

    // check form
    Object.keys(controls).forEach((controlName) => {
      controls[controlName].markAsTouched();
    });
  }

  private setAvatarStyle() {
    if (!isEmpty(this.user)) {
      const avatarUrl = `${environment.apiPrefix}/users/${this.user.id}/avatar`;

      this.http.get(avatarUrl, { responseType: 'text' }).subscribe((avatar) => {
        if (avatar) {
          avatar = avatar.replace(/(\r\n|\n|\r)/gm, '');
          this.avatarStyle.next(this.sanitizer.bypassSecurityTrustStyle(`background-image: url(${avatar});`));
        }
      });
    }
  }
}
